import { Box } from '@mui/material';
import { SelectCallToActions, SelectConversionDomain } from './FacebookFormComponents';
import type { FC } from 'react';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { SuspenseErrorFallback } from 'components/common';
import { Platform } from '../../../constants';
import { ErrorBoundary } from 'react-error-boundary';
import type { PackEntry } from 'types/shared';
import { SINGLE_AD_TYPE } from '../constants';
import { PartnershipCreationMode, PartnershipIdentityToShow } from '../types';
import {
  CreateAdsHeader,
  FormFieldSkeleton,
  FormSectionDivider,
  HotjarSection,
  PartnershipAdsRestrictionAlert,
  PartnershipAdsSwitcherRow,
  PartnershipsSectionSuspense,
} from './AdFormComponents';
import SelectAdSettingsPerPack from './SelectAdSettingsPerPack';
import { createDefaultPartnershipAdSetting } from '../utils/partnershipAdSettings';
import { isScalingEgProcess } from '../utils/marketingProcess';
import queryKeys from '../api/queryKeys';

const AdForm: FC = () => {
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext();
  const prevPartnershipAdsEnabledRef = useRef<boolean | null>(null);
  const marketingProcess = watch('marketingProcess') as string;
  const isMarketingProcessScalingEg = isScalingEgProcess(marketingProcess);
  const platform = watch('platform') as string;
  const partnershipAdsEnabled = watch('partnershipAdsEnabled') as boolean;
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[] | undefined;
  const isWebPlatform = String(platform) === String(Platform.WEB);
  const allPacksAreSingleAd = useMemo(() => {
    if (!packsWithCreatives?.length) {
      return false;
    }
    return packsWithCreatives.every((pack) => pack.adType === SINGLE_AD_TYPE);
  }, [packsWithCreatives]);
  const isPartnershipFormVisible = partnershipAdsEnabled && allPacksAreSingleAd;
  const isPartnershipRestrictionVisible = partnershipAdsEnabled && !allPacksAreSingleAd;

  useEffect(() => {
    const wasPartnershipAdsEnabled = prevPartnershipAdsEnabledRef.current;
    prevPartnershipAdsEnabledRef.current = partnershipAdsEnabled;

    if (wasPartnershipAdsEnabled && !partnershipAdsEnabled) {
      setValue('partnershipAdSettings', [createDefaultPartnershipAdSetting()], {
        shouldValidate: true,
      });
      setValue(
        'selectedAdSettings',
        packsWithCreatives?.length
          ? packsWithCreatives.map(() => ({}))
          : [{}],
        { shouldValidate: false },
      );
      setValue('partnershipIsPartnerIdentityFirst', undefined, { shouldValidate: true });
      setValue('partnershipIdentityToShow', undefined, { shouldValidate: true });
      setValue('partnershipCreationMode', undefined, { shouldValidate: true });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adSettings });
      void queryClient.invalidateQueries({ queryKey: queryKeys.partnershipAdSettings });
      return;
    }

    if (
      !wasPartnershipAdsEnabled &&
      partnershipAdsEnabled &&
      isPartnershipFormVisible
    ) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.partnershipAdSettings });
      setValue('partnershipIsPartnerIdentityFirst', true, { shouldValidate: true });
      setValue('partnershipIdentityToShow', PartnershipIdentityToShow.BOTH, { shouldValidate: true });
      setValue('partnershipCreationMode', PartnershipCreationMode.PARTNERSHIP_ONLY, { shouldValidate: true });
    }
  }, [
    isPartnershipFormVisible,
    packsWithCreatives,
    partnershipAdsEnabled,
    queryClient,
    setValue,
  ]);

  return (
    <>
      <CreateAdsHeader />
      <Box sx={{ display: 'flex', gap: 4, flexDirection: 'column', width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          {isPartnershipRestrictionVisible && <PartnershipAdsRestrictionAlert />}
          {isPartnershipFormVisible ? (
            <Box sx={{ maxWidth: '50%' }}>
              <PartnershipsSectionSuspense />
            </Box>
          ) : (
            <ErrorBoundary
              fallback={
                <SuspenseErrorFallback title="Ad Settings" message="Error occur" />
              }
            >
              <SelectAdSettingsPerPack />
            </ErrorBoundary>
          )}
        </Box>

        <Box sx={{width: '50%', display: 'flex', flexDirection: 'column', gap: 2, mb: 1}}>
          {!isMarketingProcessScalingEg && (<PartnershipAdsSwitcherRow />)}
          {isWebPlatform && <HotjarSection />}
          <FormSectionDivider />
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexDirection: 'row', width: '50%', mb: 3 }}>
          <SelectCallToActions />
          {isWebPlatform && (
            <ErrorBoundary
              fallback={
                <SuspenseErrorFallback title="Conversion Domain" message="Error occur" />
              }
            >
              <Suspense fallback={<FormFieldSkeleton />}>
                <SelectConversionDomain />
              </Suspense>
            </ErrorBoundary>
          )}
        </Box>
      </Box>
    </>
  );
};

export default AdForm;
